'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Fuse from 'fuse.js'
import { Search, X } from 'lucide-react'
import { ListingCard } from '@/components/marketing/content/listing-card'
import { ListingGrid } from '@/components/marketing/content/listing-grid'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { track } from '@/lib/analytics/track'
import {
  useCaseTemplateCategories,
  type UseCaseTemplateCategory,
} from '@/content/marketing/use-case-templates'

interface TemplateItem {
  slug: string
  title: string
  description: string
  category: UseCaseTemplateCategory
  tags: string[]
  integrations: string[]
  iconEmoji: string
}

const TEMPLATES_PER_PAGE = 12

type FilterValue = UseCaseTemplateCategory | 'all'

const categories: { label: string; value: FilterValue; emoji: string }[] = [
  { label: 'All Use Cases', value: 'all', emoji: '✦' },
  ...useCaseTemplateCategories.map(c => ({
    label: c.value,
    value: c.value as FilterValue,
    emoji: c.emoji,
  })),
]

function getPageNumbers(
  current: number,
  total: number,
): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages: (number | 'ellipsis')[] = [1]
  if (current > 3) pages.push('ellipsis')
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  if (current < total - 2) pages.push('ellipsis')
  pages.push(total)
  return pages
}

export function TemplatesListing({ templates }: { templates: TemplateItem[] }) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const activeCategory = (searchParams.get('category') || 'all') as FilterValue
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  const [query, setQuery] = useState(searchParams.get('q') || '')

  const searchTrackTimeout = useRef<ReturnType<typeof setTimeout>>(null)
  const filteredRef = useRef<TemplateItem[]>([])

  const fuse = useMemo(
    () =>
      new Fuse(templates, {
        keys: [
          { name: 'title', weight: 0.35 },
          { name: 'description', weight: 0.25 },
          { name: 'tags', weight: 0.2 },
          { name: 'integrations', weight: 0.1 },
          { name: 'category', weight: 0.1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [templates],
  )

  const filtered = useMemo(() => {
    let result = templates

    if (query.trim()) {
      result = fuse
        .search(query.trim())
        .map((r: { item: TemplateItem }) => r.item)
    }

    if (activeCategory !== 'all') {
      result = result.filter(t => t.category === activeCategory)
    }

    return result
  }, [templates, fuse, query, activeCategory])
  useEffect(() => { filteredRef.current = filtered }, [filtered])

  const totalPages = Math.max(1, Math.ceil(filtered.length / TEMPLATES_PER_PAGE))
  const page = Math.min(currentPage, totalPages)
  const paginatedTemplates = filtered.slice(
    (page - 1) * TEMPLATES_PER_PAGE,
    page * TEMPLATES_PER_PAGE,
  )
  const pageNumbers = getPageNumbers(page, totalPages)

  const activeBlurb =
    activeCategory !== 'all'
      ? useCaseTemplateCategories.find(c => c.value === activeCategory)?.blurb
      : undefined

  function buildUrl(overrides: {
    page?: number
    category?: string
    q?: string
  }) {
    const qs = new URLSearchParams()
    const p = overrides.page ?? page
    const cat = overrides.category ?? activeCategory
    const search = overrides.q ?? query

    if (p > 1) qs.set('page', String(p))
    if (cat !== 'all') qs.set('category', cat)
    if (search.trim()) qs.set('q', search.trim())

    const s = qs.toString()
    return s ? `/use-cases?${s}` : '/use-cases'
  }

  function handleSearch(value: string) {
    setQuery(value)
    const qs = new URLSearchParams()
    if (activeCategory !== 'all') qs.set('category', activeCategory)
    if (value.trim()) qs.set('q', value.trim())
    const s = qs.toString()
    router.replace(s ? `/use-cases?${s}` : '/use-cases', { scroll: false })

    if (searchTrackTimeout.current) clearTimeout(searchTrackTimeout.current)
    if (value.trim()) {
      searchTrackTimeout.current = setTimeout(() => {
        track('usecases_search', {
          query: value.trim(),
          result_count: filteredRef.current.length,
        })
      }, 500)
    }
  }

  function handleCategoryClick(cat: FilterValue) {
    track('usecases_category_filter', { category: cat })
    const qs = new URLSearchParams()
    if (cat !== 'all') qs.set('category', cat)
    if (query.trim()) qs.set('q', query.trim())
    const s = qs.toString()
    router.replace(s ? `/use-cases?${s}` : '/use-cases', { scroll: false })
  }

  return (
    <>
      {/* Category filters */}
      <div className="mt-10 flex flex-wrap gap-3">
        {categories.map(cat => {
          const isActive = cat.value === activeCategory
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleCategoryClick(cat.value)}
              className={`flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'border-foreground bg-foreground text-background shadow-md'
                  : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="text-base">{cat.emoji}</span>
              {cat.label}
            </button>
          )
        })}
      </div>

      {activeBlurb && (
        <p className="text-muted-foreground mt-6 max-w-2xl text-sm">
          {activeBlurb}
        </p>
      )}

      {/* Search input */}
      <div className="relative mt-8">
        <Search className="text-muted-foreground absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search use cases by task, platform, or role…"
          className="border-border bg-background placeholder:text-muted-foreground focus:border-foreground/30 focus:ring-foreground/10 h-11 w-full rounded-lg border pr-10 pl-11 text-sm transition-colors outline-none focus:ring-1"
        />
        {query && (
          <button
            type="button"
            onClick={() => handleSearch('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 rounded-sm p-0.5"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results count */}
      {query.trim() && (
        <p className="text-muted-foreground mt-4 text-sm">
          {filtered.length} {filtered.length === 1 ? 'result' : 'results'} for
          &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      {/* Templates grid */}
      <div className="mt-8">
        {paginatedTemplates.length > 0 ? (
          <ListingGrid>
            {paginatedTemplates.map((template, index) => (
              <ListingCard
                key={template.slug}
                href={`/use-cases/${template.slug}`}
                title={template.title}
                description={template.description}
                category={template.category}
                iconEmoji={template.iconEmoji}
                onClick={() =>
                  track('usecases_template_card_click', {
                    slug: template.slug,
                    category: template.category,
                    position: index + 1,
                    page,
                  })
                }
                meta={[
                  template.integrations.join(' · ') || undefined,
                  template.tags[0],
                ]
                  .filter(Boolean)
                  .join(' — ')}
              />
            ))}
          </ListingGrid>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground text-lg">
              No use cases found.{' '}
              {(query.trim() || activeCategory !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('')
                    handleCategoryClick('all')
                  }}
                  className="text-foreground underline underline-offset-4 hover:no-underline"
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-12">
          <Pagination>
            <PaginationContent>
              {page > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={buildUrl({ page: page - 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('usecases_paginate', {
                        page: page - 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page - 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
              {pageNumbers.map((p, i) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href={buildUrl({ page: p })}
                      isActive={p === page}
                      onClick={e => {
                        e.preventDefault()
                        track('usecases_paginate', {
                          page: p,
                          category: activeCategory,
                          query: query.trim() || undefined,
                        })
                        router.replace(buildUrl({ page: p }), { scroll: false })
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              {page < totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={buildUrl({ page: page + 1 })}
                    onClick={e => {
                      e.preventDefault()
                      track('usecases_paginate', {
                        page: page + 1,
                        category: activeCategory,
                        query: query.trim() || undefined,
                      })
                      router.replace(buildUrl({ page: page + 1 }), {
                        scroll: false,
                      })
                    }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </>
  )
}
